// TODO: Code commented out until fetchNearbyCities is fixed.
import { Cookies } from 'react-cookie';
import { takeEvery, call, put, select, fork } from 'redux-saga/effects';
import { Action } from 'redux';
import {
  getNearbyCitiesByCityUrl,
  getNonSolvPartnersSearchUrl,
  getSolvPartnersSearchUrl,
} from '../core/dapi/search';
import { safeGet } from '../core/util/object';
import { apiGet } from '../core/dapi';
import { locationResponseFormatter } from '../reducers/formatters/location';
import {
  searchInProgress,
  receivePartnersSearch,
  receiveNonPartnersSearch,
  setNearbyBrandsSearchInProgress,
  setNearbyCitiesSearchInProgress,
  receiveNearbyCitiesResponseSearch,
  receiveNearbyBrandsResponseSearch,
  receiveBestMatch,
  clearSearchResults,
} from '../ducks/searchResults';
import { getNearbyCitiesUrl } from '../core/dapi/search';
import { nearbyBrands, nearbyCities } from '../../stories/fixtures/nearby';
import {
  getBestMatchForLocations,
  getLocationsWithoutBestMatch,
  getUpdatedLocationsWithSlotsAfterTime,
  sortNonUrgentCarePartnerResults,
  sortPartnerResults,
} from '../components/Search/components/Results/util';
import {
  getRequestedPaginationForSearch,
  getSearchData,
  isPartnerSearch,
  isUrgentCareSearch,
  SEARCH_TYPE_NON_PARTNERS,
  SEARCH_TYPE_PARTNERS,
} from '../core/util/search';
import { isEmptyArray } from '../core/util/array';
import { getRequestedAppointmentTimeValue } from '../core/location/slots';
import { DEFAULT_SEARCH_RADIUS, METERS_IN_A_MILE } from '../config';
import { URGENT_CARE_VALUE } from '../components/Home/Tiles/ProviderTypes';
import { isCovidTestRelatedSearch } from '~/components/Search/components/util/search';

const FETCH_RESULTS = 'saga/SEARCH_FETCH_RESULTS';
const FETCH_MORE_RESULTS_BY_TYPE = 'saga/SEARCH_FETCH_MORE_RESULTS_BY_TYPE';
const FETCH_NEARBY_RESULTS = 'saga/SEARCH_NEARBY_RESULTS';
// const FETCH_NEARBY_CITIES = 'saga/SEARCH_FETCH_NEARBY_CITIES';

const DEFAULT_RADIUS = DEFAULT_SEARCH_RADIUS * METERS_IN_A_MILE;
const DEFAULT_PAGE = 1;

/**
 *
 */
function* searchResultsCollector(options: any) {
  const {
    resultsType,
    searchLocationPreferences,
    patientType,
    locationCategories,
    providerType,
    searchPreferencesPagination,
    isFetchingMoreResultsForSameSearch,
    resultsPagination,
    appointmentTime,
    requestedInsurance,
    requestedServices,
    reasonForVisit,
    isTelemed,
    covidCostType,
  } = options;

  let { isServiceHardFilter, isCovidTestRelatedSearch } = options;

  try {
    const requestedPagination = getRequestedPaginationForSearch(
      resultsType,
      searchPreferencesPagination,
      resultsPagination,
      isFetchingMoreResultsForSameSearch
    );

    let urlFunction;
    let paginationPage;
    let paginationRadius;

    if (isPartnerSearch(resultsType)) {
      urlFunction = getSolvPartnersSearchUrl;
      isServiceHardFilter = true; // Solv partner service searches are always hard filters
      paginationPage = (requestedPagination && requestedPagination.partnersPage) || DEFAULT_PAGE;
      paginationRadius =
        (requestedPagination && requestedPagination.partnersRadius) || DEFAULT_RADIUS;
    } else {
      urlFunction = getNonSolvPartnersSearchUrl;
      paginationPage = (requestedPagination && requestedPagination.nonPartnersPage) || DEFAULT_PAGE;
      paginationRadius =
        (requestedPagination && requestedPagination.nonPartnersRadius) || DEFAULT_RADIUS;
    }

    const url = urlFunction(
      searchLocationPreferences,
      patientType,
      locationCategories,
      providerType,
      paginationPage,
      paginationRadius,
      requestedInsurance,
      requestedServices,
      isServiceHardFilter,
      isCovidTestRelatedSearch,
      reasonForVisit,
      isTelemed,
      covidCostType
    );

    yield put(searchInProgress(resultsType, true));

    const getStateSearchResults = (state: any) => safeGet(state)('searchResults');
    const stateSearchResults = yield select(getStateSearchResults);
    const response = yield call(apiGet, url);
    const resultsAction = isPartnerSearch(resultsType)
      ? receivePartnersSearch
      : receiveNonPartnersSearch;

    const responsePagination = response.page;
    const { results } = response;

    let formattedLocations = results.map((location: any) => locationResponseFormatter(location));
    const appointmentTimeValue =
      getRequestedAppointmentTimeValue(appointmentTime) || new Date().getTime();
    if (isPartnerSearch(resultsType)) {
      formattedLocations = getUpdatedLocationsWithSlotsAfterTime(
        formattedLocations,
        appointmentTimeValue
      );
    }

    if (isPartnerSearch(resultsType) && !isFetchingMoreResultsForSameSearch) {
      const bestMatchLocations = yield* getBestMatchForLocations(formattedLocations, {
        patientType,
        providerType,
        requestedInsurance,
      });

      if (!isEmptyArray(bestMatchLocations)) {
        yield put(
          receiveBestMatch({
            response: {
              ...stateSearchResults,
              results: bestMatchLocations,
              pagination: responsePagination,
            },
          })
        );
      }

      if (providerType === URGENT_CARE_VALUE) {
        formattedLocations = sortPartnerResults(
          { booking: { requestedAppointmentTime: appointmentTimeValue } },
          getLocationsWithoutBestMatch(formattedLocations, bestMatchLocations)
        );
      } else {
        formattedLocations = sortNonUrgentCarePartnerResults(
          { booking: { requestedAppointmentTime: appointmentTimeValue } },
          requestedInsurance,
          getLocationsWithoutBestMatch(formattedLocations, bestMatchLocations)
        );
      }
    }

    const formattedResponse = {
      ...stateSearchResults,
      results: formattedLocations,
      pagination: responsePagination,
    };

    yield put(resultsAction({ response: formattedResponse }));
    yield put(searchInProgress(resultsType, false));
  } catch (e) {
    console.error('search failed', e);
    yield put(searchInProgress(resultsType, false));
  }
}

/**
 *
 */
function* nearbyResultsCollector(resultsType: any, url: any) {
  const setInProgress =
    resultsType === 'cities' ? setNearbyCitiesSearchInProgress : setNearbyBrandsSearchInProgress;
  const resultsAction =
    resultsType === 'cities'
      ? receiveNearbyCitiesResponseSearch
      : receiveNearbyBrandsResponseSearch;
  yield put(setInProgress(true));
  let response = yield call(apiGet, url);
  response.results = resultsType === 'cities' ? nearbyCities : nearbyBrands;
  yield put(resultsAction({ results: response.results }));
  yield put(setInProgress(false));
}

/**
 *
 */
function* fetchSearchResults({ resultType, isFetchingMoreResultsForSameSearch, fullReload }: any) {
  //TODO: distance matrix
  //TODO: sort client side
  //TODO: appointment slots
  //TODO: deal with no results

  const {
    searchLocationPreferences,
    patientType,
    appointmentTime,
    providerType,
    searchPreferencesPagination,
    resultsPagination,
    requestedInsurance,
    requestedServices,
    isServiceHardFilter,
    isCovidTestRelatedSearch,
    reasonForVisit,
    isTelemed,
    covidCostType,
  } = yield* getSearchData();

  const locationCategories: any = [];
  if (fullReload) yield put(clearSearchResults());

  if (isUrgentCareSearch(providerType)) {
    if (!resultType || resultType === SEARCH_TYPE_PARTNERS) {
      try {
        yield fork(searchResultsCollector, {
          resultsType: SEARCH_TYPE_PARTNERS,
          searchLocationPreferences,
          patientType,
          requestedServices,
          locationCategories,
          providerType,
          searchPreferencesPagination,
          isFetchingMoreResultsForSameSearch,
          resultsPagination,
          appointmentTime,
          requestedInsurance,
          isServiceHardFilter,
          isCovidTestRelatedSearch,
          reasonForVisit,
          isTelemed,
          covidCostType,
        });
      } catch (e) {
        yield put(searchInProgress(SEARCH_TYPE_PARTNERS, false));
      }
    }

    if (
      (!resultType || resultType === SEARCH_TYPE_NON_PARTNERS) &&
      !(isTelemed && isTelemed !== 'false') // don't do non-partner search for telemed search
    ) {
      try {
        yield fork(searchResultsCollector, {
          resultsType: SEARCH_TYPE_NON_PARTNERS,
          searchLocationPreferences,
          patientType,
          locationCategories,
          requestedServices,
          providerType,
          searchPreferencesPagination,
          isFetchingMoreResultsForSameSearch,
          resultsPagination,
          appointmentTime,
          isServiceHardFilter,
          isCovidTestRelatedSearch,
          reasonForVisit,
          covidCostType,
        });
      } catch (e) {
        yield put(searchInProgress(SEARCH_TYPE_NON_PARTNERS, false));
      }
    }
  } else {
    // SOLV-6515: Only conduct non-partner search for marketplace locations
    try {
      yield fork(searchResultsCollector, {
        resultsType: SEARCH_TYPE_NON_PARTNERS,
        searchLocationPreferences,
        patientType,
        locationCategories,
        requestedServices,
        providerType,
        searchPreferencesPagination,
        isFetchingMoreResultsForSameSearch,
        resultsPagination,
        appointmentTime,
        isServiceHardFilter,
        isCovidTestRelatedSearch,
        reasonForVisit,
        covidCostType,
      });
    } catch (e) {
      yield put(searchInProgress(SEARCH_TYPE_NON_PARTNERS, false));
    }
  }
}

/**
 *
 */
function* fetchNearbyResults({}) {
  const getSearchPreferences = (state: any) => safeGet(state, {})('searchPreferences.location');
  const searchLocationPreferences = yield select(getSearchPreferences);
  const { latitude, longitude } = searchLocationPreferences;

  yield fork(nearbyResultsCollector, 'cities', getNearbyCitiesUrl(latitude, longitude, 4, 35));
  yield fork(nearbyResultsCollector, 'brands', getNearbyCitiesUrl(latitude, longitude, 4, 35));
}

// /**
//  * Fetches cities within a radius of a city, state.
//  */
// function* fetchNearbyCities({
//   payload,
// }: Action & {
//   payload: {
//     city: string;
//     state: string;
//   };
// }) {
//   const { city, state } = payload;
//   yield fork(nearbyResultsCollector, 'cities', getNearbyCitiesByCityUrl(city, state, 4, 35));
// }

/**
 *
 */
function* rootSaga() {
  yield takeEvery(FETCH_RESULTS, fetchSearchResults);
  yield takeEvery(FETCH_MORE_RESULTS_BY_TYPE, fetchSearchResults);
  yield takeEvery(FETCH_NEARBY_RESULTS, fetchNearbyResults);
  // yield takeEvery(FETCH_NEARBY_CITIES, fetchNearbyCities);
}

export {
  FETCH_RESULTS,
  FETCH_MORE_RESULTS_BY_TYPE,
  FETCH_NEARBY_RESULTS,
  // FETCH_NEARBY_CITIES,
  rootSaga as default,
};
